import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { APPLICATION_NAMES, SCOPES } from '../../constants';
import Socket from '../../utils/Socket';

import useForceRefreshBundlesListener from './hooks/useForceRefreshBundlesListener';

const SocketContext = React.createContext();

const propTypes = {

	/**
	 * The application name.
	 */
	appName: PropTypes.oneOf([
		APPLICATION_NAMES.FARRAGUT,
		APPLICATION_NAMES.FOGGYBOT,
		APPLICATION_NAMES.PRINCEGE,
	]).isRequired,

	/**
	 * The scope.
	 */
	scope: PropTypes.oneOf([SCOPES.SESSION, SCOPES.ORGANIZATION]).isRequired,

	/**
	 * The scope ID.
	 */
	scopeId: PropTypes.string.isRequired,

	/**
	 * The component children.
	 */
	children: PropTypes.node.isRequired,

};

/**
 * The Socket provider.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object} The provider component.
 */
function SocketProvider(props) {

	const {
		appName,
		scope,
		scopeId,
		children,
	} = props;

	// Get the singleton instance of the socket for this scope
	const socket = useMemo(() => {

		console.info('---------- SocketProvider: Socket.getInstance() -------------');

		return Socket.getInstance({
			scope,
			scope_id: scopeId,
		});

	}, [scope, scopeId]);

	// Listen for force-refresh socket actions related to this application's bundles
	useForceRefreshBundlesListener(socket, appName);

	return (
		<SocketContext.Provider value={{ socket }}>
			{children}
		</SocketContext.Provider>
	);

}

SocketProvider.propTypes = propTypes;

/**
 * The socket context hook.
 *
 * @returns {Object} context         The shared context state.
 * @returns {Object} context.socket  The socket instance for this scope.
 */
function useSocketContext() {

	const context = useContext(SocketContext);

	if (!context) {

		throw new Error('useSocketContext must be used within a SocketProvider');

	}

	return context;

}

export {
	SocketProvider,
	useSocketContext, // For the newer functional components
	SocketContext, // For the older class components :(
};
